import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Calendar, Users, DollarSign, BarChart3, Settings, ChevronLeft, ChevronRight, Shield, ShieldAlert, Truck, Wrench, CalendarCheck, FolderKanban, MapPin, Tag, UserRound, Radio, MessageCircle, LogOut, Briefcase } from 'lucide-react';
import { transition, sidebar as sb } from '../../styles/tokens';
import type { User } from '../../types';
import { Logo } from '../Logo';
import { useIndustryLens } from '../../hooks/useIndustryLens';
import { getSidebarItems, getClusterLabelOverride } from './sidebarConfig';
import type { SidebarItemId } from './sidebarConfig';
import { useAuthStore } from '../../stores/authStore';
import BusinessSwitcherHeader from '../money/BusinessSwitcherHeader';
import { log } from '../../lib/logger';
import { SidebarMoneyNavItem } from './SidebarMoneyNavItem';

interface SidebarProps {
  user: User;
  overdueCount?: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavItem {
  id: string;
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}

// Master item registry — all possible sidebar items with default labels
const NAV_REGISTRY: Record<string, NavItem> = {
  dashboard: { id: 'dashboard', path: '/today', label: 'Today', icon: LayoutGrid },
  schedule: { id: 'schedule', path: '/schedule', label: 'Schedule', icon: Calendar },
  jobs: { id: 'jobs', path: '/jobs', label: 'Jobs', icon: Wrench },
  bookings: { id: 'bookings', path: '/bookings', label: 'Bookings', icon: CalendarCheck },
  dispatch: { id: 'dispatch', path: '/dispatch', label: 'Dispatch', icon: Radio },
  projects: { id: 'projects', path: '/projects', label: 'Projects', icon: FolderKanban },
  clients: { id: 'clients', path: '/clients', label: 'Clients', icon: UserRound },
  vendors: { id: 'vendors', path: '/vendors', label: 'Vendors', icon: Briefcase },
  messages: { id: 'messages', path: '/messages', label: 'Messages', icon: MessageCircle },
  services: { id: 'services', path: '/services', label: 'Services', icon: Tag },
  locations: { id: 'locations', path: '/locations', label: 'Locations', icon: MapPin },
  fleet: { id: 'fleet', path: '/fleet', label: 'Fleet', icon: Truck },
  team: { id: 'team', path: '/team', label: 'Team & Payroll', icon: Users },
  money: { id: 'money', path: '/money', label: 'Money', icon: DollarSign },
  reports: { id: 'reports', path: '/reports', label: 'Reports', icon: BarChart3 },
};

export function Sidebar({ user, overdueCount, collapsed, onToggleCollapse }: SidebarProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [collapseHov, setCollapseHov] = useState(false);
  const [signOutHov, setSignOutHov] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await useAuthStore.getState().signOut();
    } catch (e) {
      log.error('Sign out failed:', e);
    } finally {
      void navigate('/login', { replace: true });
    }
  };
  const { clusterKey } = useIndustryLens();

  const activePath = location.pathname === '/' ? '/today' : location.pathname;

  // Config-driven sidebar: cluster determines which items show and in what order
  const cluster = clusterKey;
  const isAppointmentCluster = cluster === 'A' || cluster === 'F';
  const visibleNav = useMemo(() => {
    const itemIds = getSidebarItems(cluster);
    return itemIds
      .map(id => NAV_REGISTRY[id])
      .filter(Boolean)
      .map(item => {
        // Appointment clusters: Bookings routes to /schedule (the appointment grid)
        if (isAppointmentCluster && item.id === 'bookings') {
          return { ...item, path: '/schedule' };
        }
        // Apply per-cluster label overrides (e.g., "Properties" for Cluster D)
        const override = getClusterLabelOverride(cluster, item.id as SidebarItemId);
        if (override) return { ...item, label: override };
        return item;
      });
  }, [cluster, isAppointmentCluster]);

  function NavItemComponent({ item, badge }: { item: NavItem; badge?: number }) {
    const isActive = activePath.startsWith(item.path);
    const isHov = hovered === item.id;
    const Icon = item.icon;

    return (
      <button
        title={collapsed ? item.label : undefined}
        onClick={() => navigate(item.path)}
        onMouseEnter={() => setHovered(item.id)}
        onMouseLeave={() => setHovered(null)}
        style={{
          display: 'flex', alignItems: 'center',
          gap: collapsed ? 0 : 10,
          justifyContent: collapsed ? 'center' : undefined,
          padding: collapsed ? '8px' : '8px 16px',
          margin: '1px 8px', borderRadius: 8,
          border: 'none', cursor: 'pointer',
          background: isActive ? sb.bgActive : isHov ? sb.bgHover : 'transparent',
          transition: `all ${transition.fast}`,
          position: 'relative',
          fontFamily: 'inherit',
          width: collapsed ? 48 : 'calc(100% - 16px)',
          boxSizing: 'border-box',
        }}
      >
        {isActive && (
          <div style={{
            position: 'absolute', left: -8, top: 6, bottom: 6,
            width: 3, borderRadius: '0 3px 3px 0', background: sb.accentBar,
          }} />
        )}
        <span style={{ color: isActive ? sb.textActive : isHov ? sb.textHover : sb.text, display: 'flex', transition: 'color 0.12s', flexShrink: 0, opacity: isActive ? 1 : 0.7 }}>
          <Icon size={18} strokeWidth={1.8} />
        </span>
        <span style={{
          fontSize: 13, fontWeight: isActive ? 550 : 450, letterSpacing: '-0.01em',
          color: isActive ? sb.textActive : isHov ? sb.textHover : sb.text,
          flex: 1, textAlign: 'left',
          opacity: collapsed ? 0 : 1, transition: `opacity 150ms ease`,
          width: collapsed ? 0 : undefined, overflow: 'hidden', whiteSpace: 'nowrap',
        }}>{item.label}</span>
        {!collapsed && badge !== undefined && badge > 0 && (
          <span style={{
            background: '#0F6CBD', color: '#FFFFFF',
            fontSize: 10, fontWeight: 600,
            padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center',
          }}>{badge}</span>
        )}
      </button>
    );
  }

  const initials = `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`;

  return (
    <div style={{
      width: collapsed ? sb.widthCollapsed : sb.width,
      minHeight: '100vh', background: sb.bg,
      display: 'flex', flexDirection: 'column',
      flexShrink: 0, position: 'sticky', top: 0,
      transition: 'width 200ms ease',
    }}>
      {/* Logo */}
      <div style={{ padding: collapsed ? '20px 12px 16px' : '20px 16px 16px', borderBottom: `1px solid ${sb.border}` }}>
        <div style={{
          display: 'flex',
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: collapsed ? 10 : undefined,
        }}>
          <Logo variant={collapsed ? 'mark' : 'full'} size="sm" colorScheme="white" />
          <button
            onClick={onToggleCollapse}
            onMouseEnter={() => setCollapseHov(true)}
            onMouseLeave={() => setCollapseHov(false)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              width: 28, height: 28, borderRadius: 6,
              border: `1px solid ${sb.borderSubtle}`,
              background: collapseHov ? sb.bgHover : 'transparent',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: `background ${transition.fast}`,
              flexShrink: 0,
            }}
          >
            {collapsed
              ? <ChevronRight size={14} color={sb.text} />
              : <ChevronLeft size={14} color={sb.text} />}
          </button>
        </div>
      </div>

      {/* Active-business switcher (Money Module Phase B.2). Replaces the
          legacy static "Business selector" card. Single-membership users see
          a static label (preserving the prior visual); multi-membership users
          (e.g. accountants juggling clients) get the popover affordance.
          Spec: docs/references/MONEY_MODULE_WIRING_v1.md §7.1. */}
      <div style={{
        padding: collapsed ? '8px 8px 4px' : '12px 12px 4px',
      }}>
        <BusinessSwitcherHeader collapsed={collapsed} />
      </div>

      {/* Main nav */}
      <div style={{ padding: '8px 0', flex: 1 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' as const,
          letterSpacing: '0.08em', padding: '12px 20px 6px',
          opacity: collapsed ? 0 : 1, transition: 'opacity 150ms ease',
          height: collapsed ? 0 : undefined, overflow: 'hidden',
        }}>
          Menu
        </div>
        {visibleNav.map(item =>
          item.id === 'money' ? (
            <SidebarMoneyNavItem
              key={item.id}
              item={item}
              badge={overdueCount}
              collapsed={collapsed}
              activePath={activePath}
              hovered={hovered}
              setHovered={setHovered}
              cluster={cluster}
            />
          ) : (
            <NavItemComponent key={item.id} item={item} />
          ),
        )}
        {/* Platform-admin only: the first Praetori-console surface (C2 P6). */}
        {user.is_platform_admin && (
          <NavItemComponent item={{ id: 'moderation', path: '/moderation', label: 'Moderation', icon: ShieldAlert }} />
        )}
      </div>

      {/* Footer */}
      <div style={{ borderTop: `1px solid ${sb.border}`, padding: '8px 0 12px' }}>
        <NavItemComponent item={{ id: 'settings', path: '/settings', label: 'Settings', icon: Settings }} />

        {/* User card — mirrors business header at top */}
        <div style={{
          padding: '8px 12px 4px',
          opacity: collapsed ? 0 : 1, height: collapsed ? 0 : undefined,
          overflow: 'hidden', transition: 'opacity 150ms ease, height 150ms ease',
        }}>
          <div style={{
            borderRadius: 8,
            background: sb.surfaceSubtle,
            border: `1px solid ${sb.borderSubtle}`,
            overflow: 'hidden',
          }}>
            {/* Row 1: avatar + name + role */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 16, flexShrink: 0,
                background: '#1A3355',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 600, color: sb.accentBar,
              }}>{initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: sb.textActive, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.first_name} {user.last_name}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'capitalize' as const }}>
                  {user.role}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: sb.borderSubtle }} />

            {/* Row 2: Sign out */}
            <button
              type="button"
              onClick={handleSignOut}
              onMouseEnter={() => setSignOutHov(true)}
              onMouseLeave={() => setSignOutHov(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 16px',
                width: '100%',
                border: 'none', cursor: 'pointer',
                background: signOutHov ? sb.bgHover : 'transparent',
                transition: `background ${transition.fast}`,
                fontFamily: 'inherit',
                textAlign: 'left',
                color: 'inherit',
              }}
            >
              <span style={{ color: signOutHov ? sb.textHover : sb.text, display: 'flex', flexShrink: 0, opacity: 0.7 }}>
                <LogOut size={18} strokeWidth={1.8} />
              </span>
              <span style={{
                fontSize: 13, fontWeight: 450, letterSpacing: '-0.01em',
                color: signOutHov ? sb.textHover : sb.text,
                flex: 1, textAlign: 'left', whiteSpace: 'nowrap',
              }}>Sign out</span>
            </button>
          </div>
        </div>

        {!collapsed && user.is_platform_admin && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 16px', margin: '4px 12px 0',
            borderRadius: 6, background: 'rgba(99,102,241,0.08)',
          }}>
            <Shield size={12} color="#818CF8" />
            <span style={{ fontSize: 10, fontWeight: 600, color: '#818CF8', letterSpacing: '0.08em', textTransform: 'uppercase' as const }}>
              Praetori
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
